import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import { FC, useCallback } from 'react';
import { useWizard } from 'react-use-wizard';
import { TextField } from '@/components/form/text-field';
import { ResidentRow } from '@/components/residents/resident-row';
import { Card } from '@/components/ui/card';
import { CardLabel } from '@/components/ui/card-label';
import { InfoBar, InfoBarProps } from '@/components/ui/info-bar';
import { SectionHeadingGroup } from '@/components/ui/section-heading-group';
import { SectionContainerLayout } from '@/layouts/section-container-layout';
import { composeTableOptions } from '@/table';
import { DeviceAssignedToOrganisation } from '@/types/device';
import { Resident } from '@/types/resident';
import {
  getDeviceDisplayName,
  getDeviceTypeLabel,
} from '@/utils/device-helpers';
import { NewUnitWizardFooter } from '../new-unit-wizard-footer';

const columns: MRT_ColumnDef<DeviceAssignedToOrganisation>[] = [
  {
    id: 'displayName',
    accessorFn: (device) => getDeviceDisplayName(device),
    header: 'Device Name',
  },
  {
    id: 'serialNumber',
    accessorFn: ({ meta }) => meta?.serialNumber,
    header: 'Device Serial Number (DSN)',
  },
  {
    id: 'type',
    accessorFn: ({ type }) => getDeviceTypeLabel(type),
    header: 'Type',
  },
];

export type ConfirmationStepProps = {
  unitName: string | undefined;
  selectedDevices: DeviceAssignedToOrganisation[];
  selectedResidents: Resident[];
  interactionDisabled: boolean;
  allowSkip: boolean;
  residentStepIndex: number;
  statusMessage?: string;
  statusMessageVariant: InfoBarProps['variant'];
  onConfirm: () => void;
  onSkip: () => void;
};

export const ConfirmationStep: FC<ConfirmationStepProps> = ({
  unitName,
  selectedDevices,
  selectedResidents,
  allowSkip,
  interactionDisabled,
  residentStepIndex,
  statusMessage,
  statusMessageVariant,
  onConfirm,
  onSkip,
}) => {
  const { goToStep } = useWizard();
  const table = useMaterialReactTable({
    ...composeTableOptions<DeviceAssignedToOrganisation>({
      enableRowActions: true,
    }),
    columns,
    enableColumnResizing: true,
    data: selectedDevices,
  });

  const goToResidentsStep = useCallback(() => {
    goToStep(residentStepIndex);
  }, [residentStepIndex, goToStep]);

  return (
    <>
      <SectionContainerLayout horizontal className="m-0 border-none p-0">
        <SectionHeadingGroup
          className="max-w-96"
          subtitle="Review the details of this unit before finalising."
          title="Confirm unit setup"
        />

        <Card className="flex flex-col">
          <TextField readOnly label="Unit Name" value={unitName} />
          <CardLabel>Resident(s) to be associated:</CardLabel>

          <div className="flex flex-col border-t border-gray-200">
            {selectedResidents?.map((resident, index) => (
              <ResidentRow
                actionText="Change"
                key={index}
                onActionClick={goToResidentsStep}
                resident={resident}
              />
            ))}
          </div>

          <CardLabel>Device(s) to be associated:</CardLabel>

          <MaterialReactTable table={table} />

          <div className="mt-7">
            {statusMessage ? (
              <InfoBar text={statusMessage} variant={statusMessageVariant} />
            ) : (
              <InfoBar text="If all details are correct, click “Confirm” to finalise setup." />
            )}
          </div>
        </Card>
      </SectionContainerLayout>
      <NewUnitWizardFooter
        interactionDisabled={interactionDisabled}
        onConfirm={onConfirm}
        onSkip={onSkip}
        skipable={allowSkip}
      />
    </>
  );
};
